/**
 * Disclosure
 *
 * The collapsible section used on the Billing page's plan block: a heading whose button toggles a
 * body (44px target, aria-expanded / aria-controls, rotating chevron). Controlled, so a caller can
 * pick the initial state.
 */

import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DisclosureProps {
  /** Id prefix: the heading is `${id}-heading` and the body `${id}-body`. */
  id: string
  /** data-testid of the toggle button. */
  toggleTestId: string
  label: ReactNode
  open: boolean
  onToggle: () => void
  children: ReactNode
}

export function Disclosure({ id, toggleTestId, label, open, onToggle, children }: DisclosureProps) {
  return (
    <section aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`} className="m-0">
        <button
          type="button"
          className="flex min-h-[44px] w-full items-center gap-2 border-0 bg-transparent p-0 text-left text-sm font-semibold text-foreground"
          aria-expanded={open}
          aria-controls={`${id}-body`}
          data-testid={toggleTestId}
          onClick={onToggle}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              !open && '-rotate-90'
            )}
            aria-hidden="true"
          />
          {label}
        </button>
      </h3>

      <div id={`${id}-body`} hidden={!open}>
        {children}
      </div>
    </section>
  )
}
